import { shouldIncludeAttributes } from "@tiptap-content/util/markdown-options"
import { mergeAttributes, Node, type JSONContent } from "@tiptap/core"
import type { DOMOutputSpec } from "@tiptap/pm/model"
import * as z from "zod"
import {
  escapeXml,
  formatXmlAttributes,
  parseXmlAttributes,
  XML_ATTRIBUTES_SOURCE,
} from "../../util/markdown-xml-attributes"
import { toTiptapAttributes } from "../../util/render-data"

export const LessonStandardSchema = z.object({
  id: z.string(),
  standardSetId: z.string(),
  code: z.string().nullable().default(null),
  statement: z.string().default(""),
  jurisdiction: z.string().default(""),
  grades: z.string().default(""),
  subject: z.string().default(""),
})
export type LessonStandardAttrs = z.infer<typeof LessonStandardSchema>

export const StandardAttrsSchema = z.object({
  standard: LessonStandardSchema,
  emphasisLevel: z.enum(["STARRED", "EXPANDED"]).nullable().default(null),
})
export type ExtensionStandardAttrs = z.infer<typeof StandardAttrsSchema>

// Only these are read back; the rest of the standard is filled in from its standard set.
const StandardXmlAttrsSchema = z.object({
  id: z.string(),
  standardSetId: z.string(),
  emphasisLevel: z.enum(["STARRED", "EXPANDED"]).optional().catch(undefined),
})

const UNKNOWN_STANDARD_ID = "unknown"
// The text is escaped on render, so the first "</standard>" is this tag's.
const STANDARD_TAG = new RegExp(
  `^ {0,3}<standard(${XML_ATTRIBUTES_SOURCE})\\s*(?:/>|>[\\s\\S]*?</standard>)[ \\t]*(?:\\n|$)`,
  "i",
)

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    standard: {
      /**
       * Deletes every standard node whose standard id is in `ids`.
       *
       * Positions are found against the transaction's own document rather than the editor's, so
       * this can be chained AFTER an insertion in the same transaction — which is what lets one
       * standards edit (the adds and the removals together) be a single mutation, and so carry a
       * single event.
       */
      deleteStandardsByIds: (ids: Iterable<string>) => ReturnType
    }
  }
}

export const StandardContentExtension = Node.create({
  name: "standard",
  group: "block widget",
  draggable: true,

  addAttributes: () => toTiptapAttributes(StandardAttrsSchema),

  renderHTML({ node, HTMLAttributes }) {
    const { standard, emphasisLevel } = node.attrs as ExtensionStandardAttrs

    const star: Array<DOMOutputSpec> =
      emphasisLevel === "STARRED" ? [["span", { class: "standard-star" }, "★ "]] : []
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-content-type": "standard",
        class: "standard-row",
      }),
      ...star,
      ["span", { class: "standard-code" }, standard.code ?? ""],
      ["span", {}, standard.statement ?? ""],
      [
        "span",
        { class: "standard-set-info" },
        `${standard.jurisdiction}. ${standard.subject}. ${standard.grades}`,
      ],
    ]
  },

  parseHTML() {
    return [{ tag: 'div[data-content-type="standard"]' }]
  },

  markdownTokenName: "standard",
  markdownTokenizer: {
    name: "standard",
    level: "block",
    // Like a CommonMark HTML block, a standard can't interrupt a paragraph.
    start: () => -1,
    tokenize(src) {
      const match = STANDARD_TAG.exec(src)
      if (!match) return
      const attributes = parseXmlAttributes(match[1], StandardXmlAttrsSchema)
      if (!attributes) return
      return { type: "standard", raw: match[0], attributes }
    },
  },
  parseMarkdown: (token): JSONContent => {
    const { id, standardSetId, emphasisLevel } = token.attributes as z.output<
      typeof StandardXmlAttrsSchema
    >
    return {
      type: "standard",
      attrs: StandardAttrsSchema.parse({
        standard: { id: id || UNKNOWN_STANDARD_ID, standardSetId },
        emphasisLevel,
      }),
    }
  },
  renderMarkdown(node: JSONContent): string {
    const { standard, emphasisLevel } = StandardAttrsSchema.parse(node.attrs)
    const attributes = formatXmlAttributes({
      id: standard.id,
      standardSetId: standard.standardSetId,
      code: standard.code,
      subject: standard.subject,
      grades: standard.grades,
      emphasisLevel,
    })

    const jurisdiction = standard.jurisdiction ? ` (${standard.jurisdiction})` : ""
    const text = [standard.code, standard.statement].filter(Boolean).join(" ") + jurisdiction
    if (shouldIncludeAttributes()) {
      return text
        ? `<standard${attributes}>${escapeXml(text)}</standard>`
        : `<standard${attributes} />`
    } else {
      return escapeXml(text)
    }
  },

  addCommands() {
    return {
      deleteStandardsByIds:
        (ids) =>
        ({ tr }) => {
          const idSet = new Set(ids)
          if (idSet.size === 0) return true

          const positions: Array<number> = []
          tr.doc.descendants((node, pos) => {
            if (
              node.type.name === "standard" &&
              idSet.has((node.attrs as ExtensionStandardAttrs).standard.id)
            ) {
              positions.push(pos)
            }
          })

          // Delete from last to first so earlier positions stay valid as we go.
          for (let i = positions.length - 1; i >= 0; i--) {
            const pos = positions[i]
            const node = tr.doc.nodeAt(pos)
            if (node) tr.delete(pos, pos + node.nodeSize)
          }
          return true
        },
    }
  },
})
